import { useEffect, useMemo, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ShiftTimeEntryRow } from "./ShiftTimeEntryRow";
import { TimezoneIndicator } from "./TimezoneIndicator";
import { useSubmitTimeEntries } from "../hooks/useSubmitTimeEntries";
import {
  weeklyTimeEntryFormSchema,
  type WeeklyTimeEntryFormData,
  createDefaultTimeBlock,
  calculateDurationHours,
} from "../utils/time-entry-schema";
import { getDeadlineText } from "../utils/week-utils";
import { type WeeklyShiftGroup } from "../hooks/useTimeEntryShifts";
import { WageFrequency } from "@api/shared-runtime/enums";

interface TimeEntryWeekFormProps {
  weekGroup: WeeklyShiftGroup;
  onSubmitSuccess?: () => void;
}

export function TimeEntryWeekForm({
  weekGroup,
  onSubmitSuccess,
}: TimeEntryWeekFormProps) {
  const { weekKey, shifts } = weekGroup;
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate: submitEntries, isPending: isSubmitting } =
    useSubmitTimeEntries();

  // Keep deadline calculations tied to the jobs' shared timezone when possible.
  const commonTimezone = useMemo(() => {
    if (shifts.length === 0) {
      return null;
    }
    const firstTimezone = shifts[0].timezoneId;
    const allSame = shifts.every((s) => s.timezoneId === firstTimezone);
    return allSame ? firstTimezone : null;
  }, [shifts]);

  // Create default form values from shifts
  const defaultValues = useMemo<WeeklyTimeEntryFormData>(() => {
    return {
      weekKey,
      shifts: shifts.map((shift) => {
        // If there are existing entries that are rejected, use those times
        // Otherwise use the scheduled times
        const hasRejectedEntries =
          shift.existingEntries?.some((e) => e.status === "rejected") ?? false;

        const defaultBlock = createDefaultTimeBlock(
          new Date(shift.scheduledStartsAt),
          new Date(shift.scheduledEndsAt),
          shift.suggestedWageType,
        );

        // If there are rejected entries, use those values instead
        if (
          hasRejectedEntries &&
          shift.existingEntries &&
          shift.existingEntries.length > 0
        ) {
          const rejectedEntry = shift.existingEntries.find(
            (e) => e.status === "rejected",
          );
          if (rejectedEntry) {
            defaultBlock.startsAt = new Date(
              rejectedEntry.startsAt,
            ).toISOString();
            defaultBlock.endsAt = new Date(rejectedEntry.endsAt).toISOString();
            defaultBlock.baseWageType = shift.allowedBaseWageTypes.includes(
              rejectedEntry.baseWageType,
            )
              ? rejectedEntry.baseWageType
              : shift.suggestedWageType;
            defaultBlock.isOvertime =
              rejectedEntry.isOvertime &&
              shift.overtimeWageTypes.includes(defaultBlock.baseWageType);
            defaultBlock.providerNote = rejectedEntry.providerNote || "";
          }
        }

        return {
          shiftId: shift.shiftId,
          shiftDate: new Date(shift.scheduledStartsAt),
          timeBlocks: [defaultBlock],
        };
      }),
    };
  }, [weekKey, shifts]);

  const form = useForm<WeeklyTimeEntryFormData>({
    resolver: zodResolver(weeklyTimeEntryFormSchema),
    defaultValues,
  });
  const previousDefaults = useRef(defaultValues);

  // Reconcile by ID: withdrawing one row can shift every following index.
  // Keep edits on rows whose server defaults did not change, while accepting
  // updated defaults and removing rows no longer in the submission queue.
  useEffect(() => {
    const currentValues = form.getValues();
    const currentById = new Map(
      currentValues.shifts.map((shift) => [shift.shiftId, shift]),
    );
    const previousById = new Map(
      previousDefaults.current.shifts.map((shift) => [shift.shiftId, shift]),
    );
    form.reset({
      ...defaultValues,
      shifts: defaultValues.shifts.map((shift) => {
        if (
          currentValues.weekKey === defaultValues.weekKey &&
          JSON.stringify(previousById.get(shift.shiftId)) ===
            JSON.stringify(shift)
        ) {
          return currentById.get(shift.shiftId) ?? shift;
        }
        return shift;
      }),
    });
    previousDefaults.current = defaultValues;
  }, [defaultValues, form]);

  const withdrawal = useMutation(
    trpc.timeEntry.withdrawPendingTimeEntry.mutationOptions({
      onMutate: async () => {
        await queryClient.cancelQueries({
          queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
        });
      },
      onSuccess: async (_result, { shiftId }) => {
        // Remove it before refetching so a slow or failed refresh cannot leave
        // withdrawn hours in a subsequent submission.
        const currentValues = form.getValues();
        form.reset({
          ...currentValues,
          shifts: currentValues.shifts.filter(
            (shift) => shift.shiftId !== shiftId,
          ),
        });
        toast.success(
          "Added hours withdrawn. You can add them again with a different wage set.",
        );
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.timeEntry.getShiftsNeedingEntries.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.timeEntry.getSubmittedTimeEntries.queryKey(),
          }),
        ]);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to withdraw hours");
      },
    }),
  );

  // Calling watch subscribes the form to edits so the total stays current.
  const watchedValues = form.watch();
  let totalHours = 0;
  for (const shift of watchedValues.shifts) {
    for (const block of shift.timeBlocks) {
      totalHours += calculateDurationHours(block.startsAt, block.endsAt);
    }
  }

  const handleSubmit = form.handleSubmit((data) => {
    if (withdrawal.isPending || isSubmitting) {
      return;
    }
    const shiftById = new Map(shifts.map((shift) => [shift.shiftId, shift]));

    // Transform form data to API format
    // startsAt and endsAt are now ISO datetime strings
    const entries = data.shifts
      .filter((shift) => shiftById.has(shift.shiftId))
      .map((formShift) => {
        const shift = shiftById.get(formShift.shiftId);
        const isOneTime = shift?.wageFrequencyId === WageFrequency.OneTime;
        const overtimeWageTypes = shift?.overtimeWageTypes ?? [];
        const timeBlocks = isOneTime
          ? formShift.timeBlocks.slice(0, 1)
          : formShift.timeBlocks;

        return {
          shiftId: formShift.shiftId,
          timeBlocks: timeBlocks.map((block) => ({
            startsAt: new Date(block.startsAt),
            endsAt: new Date(block.endsAt),
            baseWageType: block.baseWageType,
            isOvertime:
              block.isOvertime &&
              overtimeWageTypes.includes(block.baseWageType),
            providerNote: block.providerNote,
          })),
        };
      });

    if (entries.length === 0) {
      return;
    }

    submitEntries(
      { weekKey, entries },
      {
        onSuccess: () => {
          onSubmitSuccess?.();
        },
      },
    );
  });

  const deadline = getDeadlineText(weekKey, commonTimezone ?? undefined);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        {/* Time entries use the timezone of their associated job offer. */}
        {commonTimezone && (
          <TimezoneIndicator timezoneId={commonTimezone} className="mb-4" />
        )}

        {/* Due date banner - only show if overdue or due soon */}
        {(deadline === "Overdue" || deadline === "Due today") && (
          <div
            className={cn(
              "mb-4 px-3 py-2 rounded-lg text-sm font-medium text-center",
              deadline === "Overdue" && "bg-red-100 text-red-700",
              deadline === "Due today" && "bg-amber-100 text-amber-700",
            )}
          >
            {deadline === "Overdue"
              ? "⚠️ Overdue - please submit"
              : "📋 Due today"}
          </div>
        )}

        {/* Shifts List */}
        <div className="space-y-4 mb-6">
          {watchedValues.shifts.map((formShift, index) => {
            const shift = shifts.find(
              (candidate) => candidate.shiftId === formShift.shiftId,
            );
            if (!shift) {
              return null;
            }
            return (
              <ShiftTimeEntryRow
                key={`${shift.shiftId}-${index}`}
                shiftIndex={index}
                shift={shift}
                onWithdraw={() => withdrawal.mutate({ shiftId: shift.shiftId })}
                isWithdrawing={
                  withdrawal.isPending &&
                  withdrawal.variables?.shiftId === shift.shiftId
                }
                disabled={isSubmitting || withdrawal.isPending}
              />
            );
          })}
        </div>

        {/* Summary & Actions Footer */}
        <div className="bg-white border-t border-slate-200 pt-4 mt-6">
          <div className="flex items-center justify-between gap-4">
            {/* Total hours */}
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-400" />
              <span className="text-lg font-semibold text-slate-700">
                Total: {totalHours.toFixed(1)} hours
              </span>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                withdrawal.isPending ||
                watchedValues.shifts.length === 0
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Submit Time Entries
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
